import React, { useState } from "react";
import CurrencyInput from "../CurrencyInput";
import {
  buildUnitConversionPreview,
  createUnitDraft,
  getBaseUnitDisplayName,
  validateProductUnits,
} from "../../utils/inventoryUnits";

export default function ProductUnitsEditor({
  units,
  onChange,
  mainPrice = "",
  allowActive = false,
  generateBarcode,
  disabled = false,
  showValidationErrors = false,
}) {
  const [touchedRows, setTouchedRows] = useState({});
  const errors = validateProductUnits(units);
  const preview = buildUnitConversionPreview(units);
  const baseUnitPlural = getBaseUnitDisplayName(units);
  const hasTouchedRows = Object.keys(touchedRows).length > 0;
  const displayUnits = units
    .map((unit, index) => ({ unit, index }))
    .sort((left, right) =>
      Number(Boolean(right.unit.is_base_unit)) -
      Number(Boolean(left.unit.is_base_unit)));

  const markTouched = (index) => {
    setTouchedRows((current) => ({ ...current, [index]: true }));
  };

  const updateUnit = (index, field, value) => {
    onChange(units.map((unit, unitIndex) => {
      if (unitIndex !== index) return unit;
      if (field === "is_base_unit" && value) {
        return { ...unit, is_base_unit: true, conversion_factor: 1 };
      }
      return { ...unit, [field]: value };
    }).map((unit, unitIndex) =>
      field === "is_base_unit" && value && unitIndex !== index
        ? { ...unit, is_base_unit: false }
        : unit));
  };

  const addUnit = () => {
    onChange([...units, createUnitDraft({ selling_price: mainPrice })]);
  };

  const removeUnit = (index) => {
    const unit = units[index];
    if (unit.id) {
      const confirmed = window.confirm(
        `${unit.unit_name || "This unit"} may appear in transaction history. ` +
        "It will be deactivated instead of deleted. Continue?",
      );
      if (!confirmed) return;
      updateUnit(index, "is_active", false);
      return;
    }
    onChange(units.filter((_, unitIndex) => unitIndex !== index));
  };

  return (
    <section className="packaging-units-editor" aria-label="Packaging unit configuration">
      <div className="packaging-units-toolbar">
        <p>
          Stock is counted in the base unit. Every packaging unit shares the
          same stock balance.
        </p>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={addUnit}
          disabled={disabled}
        >
          + Add another unit
        </button>
      </div>

      {(showValidationErrors || hasTouchedRows) &&
        (errors.base || errors.names || errors.barcodes) && (
        <div className="form-error packaging-units-error" role="alert">
          {[errors.base, errors.names, errors.barcodes].filter(Boolean).join(" ")}
        </div>
      )}

      <div className="unit-definition-list">
        {displayUnits.map(({ unit, index }) => {
          const prefix = `packaging-unit-${unit.id || index}`;
          const isBase = Boolean(unit.is_base_unit);
          const rowErrors = errors.rows?.[index] || [];
          return (
            <div
              key={unit.id || `new-${index}`}
              className={`unit-definition ${unit.is_active === false ? "is-inactive" : ""}`}
            >
              {isBase && <span className="unit-base-badge">BASE UNIT</span>}

              <label htmlFor={`${prefix}-name`}>
                Unit name
                <input
                  id={`${prefix}-name`}
                  type="text"
                  autoComplete="off"
                  value={unit.unit_name}
                  disabled={disabled || unit.is_active === false}
                  onChange={(event) => updateUnit(index, "unit_name", event.target.value)}
                  onBlur={() => markTouched(index)}
                  placeholder={isBase ? "Sachet, Stick, Bottle…" : "Packet, Carton, Box…"}
                />
              </label>

              <label htmlFor={`${prefix}-factor`}>
                {isBase
                  ? "Base-unit factor"
                  : `Contains how many ${baseUnitPlural}?`}
                <input
                  id={`${prefix}-factor`}
                  type="number"
                  autoComplete="off"
                  min="1"
                  step="1"
                  value={isBase ? 1 : unit.conversion_factor}
                  disabled={disabled || isBase || unit.is_active === false}
                  onChange={(event) => updateUnit(index, "conversion_factor", event.target.value)}
                  onBlur={() => markTouched(index)}
                />
                <small>{isBase ? "Fixed at 1" : `Counted in ${baseUnitPlural}`}</small>
              </label>

              <label htmlFor={`${prefix}-barcode`}>
                Unit barcode
                <div className="unit-barcode-field">
                  <input
                    id={`${prefix}-barcode`}
                    type="text"
                    autoComplete="off"
                    value={unit.barcode || ""}
                    disabled={disabled || unit.is_active === false}
                    onChange={(event) => updateUnit(index, "barcode", event.target.value)}
                    onBlur={() => markTouched(index)}
                    placeholder="Generated if blank"
                  />
                  {generateBarcode && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => generateBarcode(index)}
                      disabled={disabled || unit.is_active === false}
                    >
                      Generate
                    </button>
                  )}
                </div>
                <small>
                  One reusable barcode for this unit type
                </small>
                {(unit.barcode_is_shared || unit.shared_barcode_count > 1) && (
                  <span className="unit-shared-barcode-note">
                    <span className="unit-shared-barcode-badge">Shared barcode</span>
                    {unit.shared_barcode_count > 1 && (
                      <span>{unit.shared_barcode_count} products</span>
                    )}
                  </span>
                )}
              </label>

              <label htmlFor={`${prefix}-price`}>
                Selling price
                <CurrencyInput
                  id={`${prefix}-price`}
                  value={unit.selling_price}
                  onChange={(value) => updateUnit(index, "selling_price", value)}
                  step="50"
                  min="0"
                  autoComplete="off"
                  disabled={disabled || unit.is_active === false}
                  label={`${unit.unit_name || `unit ${index + 1}`} selling price`}
                  onBlur={() => markTouched(index)}
                />
              </label>

              {(!isBase || (allowActive && unit.id)) && (
                <div className="unit-definition-options">
                  {!isBase && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm unit-set-base-btn"
                      disabled={disabled || unit.is_active === false}
                      onClick={() => updateUnit(index, "is_base_unit", true)}
                    >
                      Set as base unit
                    </button>
                  )}
                  {allowActive && unit.id && (
                    <label>
                      <input
                        type="checkbox"
                        checked={unit.is_active !== false}
                        onChange={(event) => updateUnit(index, "is_active", event.target.checked)}
                      />
                      Active
                    </label>
                  )}
                </div>
              )}

              {(showValidationErrors || touchedRows[index]) && rowErrors.length > 0 && (
                <ul className="unit-definition-errors" role="alert">
                  {rowErrors.map((message) => <li key={message}>{message}</li>)}
                </ul>
              )}

              {units.length > 1 && unit.is_active !== false && (
                <div className="unit-definition-actions">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm unit-remove-btn"
                    onClick={() => removeUnit(index)}
                  >
                    {unit.id ? "Deactivate unit" : "Remove unit"}
                  </button>
                </div>
              )}
              {unit.is_active === false && (
                <p className="unit-inactive-note">Inactive — retained for transaction history.</p>
              )}
            </div>
          );
        })}
      </div>

      {preview.length > 0 && (
        <div className="conversion-preview packaging-conversion-preview">
          <strong>Conversion preview</strong>
          <ul>{preview.map((line) => <li key={line}>{line}</li>)}</ul>
        </div>
      )}
    </section>
  );
}
